/**
 * Saved-view lifecycle for the Legislation page (#1144), extracted from
 * LegislationPage (which had grown past 2,000 lines): applying a view,
 * per-tab reload persistence, primary auto-apply, drift detection, the
 * Tracked/All override, wizard open/edit state, and everything the bills
 * request and BillTable derive from the applied view.
 *
 * The page stays the owner of its filters/search/scope state and URL sync;
 * this hook receives those setters and a `onBeforeApply` callback (the page
 * marks its pending-URL-sync ref there). Rules preserved verbatim:
 *
 * - Applying a view sets filters + search + scope together (R9), coercing
 *   pre-multi-select string shapes via toFilterArray — stored JSON is an
 *   unchecked cast and must be validated where read.
 * - The applied view survives F5 per-tab (sessionStorage) WITHOUT clobbering
 *   URL-parsed filters — manual tweaks read as "(modified)".
 * - The primary view auto-applies only on a bare visit; deep links win.
 * - trackedOverride is ephemeral (toolbar toggle) and resets on apply/detach.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSavedViews } from './useSavedViews';
import type { SavedView } from '../types/savedViews';
import { customFieldIdFromColId } from '../types/savedViews';
import { savedViewToBillFilters } from '../utils/savedViewFilters';
import type { BillFilters } from './useBillFilters';
import type { LegislationSearchScope } from '../utils/legislationUrlState';
import type { CustomFieldDefinition } from '../types/custom-fields';

const APPLIED_VIEW_STORAGE_KEY = 'legislationAppliedViewId';

export interface UseSavedViewStateArgs {
  filters: BillFilters;
  setFilters: React.Dispatch<React.SetStateAction<BillFilters>>;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  setSearchScope: (s: LegislationSearchScope) => void;
  customFieldDefs: CustomFieldDefinition[];
  urlParsingComplete: boolean;
  locationSearch: string;
  /** Called before any state change that must not bounce the URL-sync effect. */
  onBeforeApply: () => void;
}

export function useSavedViewState({
  filters,
  setFilters,
  searchQuery,
  setSearchQuery,
  setSearchScope,
  customFieldDefs,
  urlParsingComplete,
  locationSearch,
  onBeforeApply,
}: UseSavedViewStateArgs) {
  const savedViews = useSavedViews();
  const [appliedView, setAppliedView] = useState<SavedView | null>(null);
  const [appliedSnapshot, setAppliedSnapshot] = useState<string>('');
  const [trackedOverride, setTrackedOverride] = useState<boolean | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEditingView, setWizardEditingView] = useState<SavedView | null>(null);

  /* What the applied view contributes to the bills request. */
  const viewFetchParams = useMemo(() => {
    if (!appliedView) return null;
    const columns = appliedView.columns;
    const hasWorkspaceCols =
      (columns?.workspaceColumns ?? []).filter((c) => !c.hide).length > 0;
    if (!hasWorkspaceCols || appliedView.workspaceIds.length === 0) {
      return (trackedOverride ?? columns?.trackedBillsOnly ?? true) && appliedView.workspaceIds.length > 0
        ? { workspaceIds: appliedView.workspaceIds, customFieldIds: [] as string[], trackedOnly: true }
        : null;
    }
    const customFieldIds = (columns?.workspaceColumns ?? [])
      .map((c) => customFieldIdFromColId(c.colId))
      .filter((id): id is string => !!id);
    return {
      workspaceIds: appliedView.workspaceIds,
      customFieldIds,
      trackedOnly: trackedOverride ?? columns?.trackedBillsOnly ?? true,
    };
  }, [appliedView, trackedOverride]);

  const savedViewForTable = useMemo(() => {
    if (!appliedView?.columns) return undefined;
    const customFieldNames: Record<string, string> = {};
    const customFieldDescriptions: Record<string, string> = {};
    for (const def of customFieldDefs) {
      customFieldNames[def.id] = def.name;
      if (def.description) customFieldDescriptions[def.id] = def.description;
    }
    return {
      columns: appliedView.columns,
      workspaceCount: appliedView.workspaceIds.length,
      customFieldNames,
      customFieldDescriptions,
    };
  }, [appliedView, customFieldDefs]);

  /* Nested rendering is active with visible workspace columns across 2+
     workspaces — the same gate BillTable uses (R21/R22). */
  const nestedViewActive =
    !!savedViewForTable &&
    savedViewForTable.workspaceCount > 1 &&
    savedViewForTable.columns.workspaceColumns.some((c) => !c.hide);

  /*
    Tracked is the DEFAULT (QA round 9): a view with no stored preference
    opens on Tracked bills; a view saved with All bills keeps All. The
    toolbar override still beats both.
  */
  const effectiveTrackedMode =
    trackedOverride ?? appliedView?.columns?.trackedBillsOnly ?? true;

  const applySavedView = (filter: SavedView) => {
    onBeforeApply();
    const applied = savedViewToBillFilters(filters, filter);
    const merged = applied.filters;

    setFilters(merged);
    setSearchQuery(applied.searchQuery);
    setSearchScope(applied.searchScope);

    savedViews.setActiveViewId(filter.id);
    setAppliedView(filter);
    setTrackedOverride(null);
    // Survives F5 in this tab — see the restoration effect below.
    sessionStorage.setItem(APPLIED_VIEW_STORAGE_KEY, filter.id);
    // Drift detection for the "(modified)" marker (R16).
    setAppliedSnapshot(JSON.stringify({ f: merged, q: filter.searchQuery || '' }));
  };

  /** Deselect the view (Default View / Clear filters); page resets its own filters. */
  const detachView = () => {
    savedViews.setActiveViewId(null);
    setAppliedView(null);
    setAppliedSnapshot('');
    setTrackedOverride(null);
    sessionStorage.removeItem(APPLIED_VIEW_STORAGE_KEY);
  };

  const savedViewIsModified =
    appliedView !== null &&
    appliedSnapshot !== JSON.stringify({ f: filters, q: searchQuery });

  /*
    View restoration on load, in priority order:
    1. The view applied in THIS tab before a reload (sessionStorage). The URL
       already carries the view's filters, so refreshing used to keep the
       filters but silently drop the view — no sub-records, no columns
       (found live on dev). Restoring re-attaches the view WITHOUT touching
       the URL-parsed filters, so manual tweaks survive as "(modified)".
    2. The primary view (R12) — but only on a bare visit; deep links win.
  */
  const primaryAppliedRef = useRef(false);
  useEffect(() => {
    if (primaryAppliedRef.current) return;
    if (!urlParsingComplete || !savedViews.loaded) return;
    primaryAppliedRef.current = true;
    if (appliedView) return;

    const storedId = sessionStorage.getItem(APPLIED_VIEW_STORAGE_KEY);
    const stored = storedId ? savedViews.views.find((v) => v.id === storedId) : null;
    if (storedId && !stored) sessionStorage.removeItem(APPLIED_VIEW_STORAGE_KEY);
    if (stored) {
      savedViews.setActiveViewId(stored.id);
      setAppliedView(stored);
      // Snapshot the VIEW's own state; URL filters that differ read as modified.
      setAppliedSnapshot(
        JSON.stringify({
          f: { ...filters, ...(stored.filters as Partial<BillFilters>) },
          q: stored.searchQuery || '',
        })
      );
      return;
    }

    const bare =
      !locationSearch || locationSearch === '' || /^\?session=\d+$/.test(locationSearch);
    if (!bare) return;
    const primary = savedViews.primaryView;
    if (primary) applySavedView(primary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParsingComplete, savedViews.loaded]);

  /* Wizard entry points (D5: the wizard is the only authoring path). */
  const openNewWizard = () => {
    setWizardEditingView(null);
    setWizardOpen(true);
  };
  const openEditWizard = (view: SavedView) => {
    setWizardEditingView(view);
    setWizardOpen(true);
  };
  const handleWizardSaved = (view: SavedView) => {
    savedViews.upsert(view);
    applySavedView(view);
  };
  /*
    Snapshot of the ONE tracked-bills control (the toolbar toggle). Editing a
    view that is not currently applied keeps that view's own stored flag —
    the live toggle belongs to the applied view.
  */
  const wizardTrackedBillsOnly =
    wizardEditingView && wizardEditingView.id !== appliedView?.id
      ? wizardEditingView.columns?.trackedBillsOnly ?? false
      : effectiveTrackedMode;

  return {
    savedViews,
    appliedView,
    applySavedView,
    detachView,
    savedViewIsModified,
    viewFetchParams,
    savedViewForTable,
    nestedViewActive,
    effectiveTrackedMode,
    setTrackedOverride,
    wizardOpen,
    setWizardOpen,
    wizardEditingView,
    openNewWizard,
    openEditWizard,
    handleWizardSaved,
    wizardTrackedBillsOnly,
  };
}
